import { log } from './logger';
import { EnvironmentError, errorMessage } from './errors';
import { browserAudioPlatform } from './audioPlatform';
import type { AudioPlatform, MediaStreamLike } from './audioPlatform';

/**
 * §8.1 — **the microphone stream lifecycle**, and AUD-4's permission prompt (build unit U-11).
 *
 * ── The stream is held for the match, not acquired per recording ─────────────
 *
 * > *"The stream is then acquired on entry to an in-match state and held for the duration of
 * > in-match states; released on exit. It is never held outside in-match states (NFR-5). Because
 * > permission is already granted for an installed PWA, acquisition costs nothing at Kickoff, so
 * > FLAG-1's 'starts recording immediately with no confirmation' holds from the first tap."*
 *
 * The alternative — acquire per recording — was rejected in the design because `getUserMedia` costs
 * 200–800 ms on iOS, so *"a per-recording acquisition loses the opening words of every flag, which
 * is the part that says what happened."* The price is the iOS recording indicator lit for the match,
 * which the design calls **honest** (the app genuinely can record at any instant) and VER-4
 * measures the battery delta of.
 *
 * **NFR-5 is satisfied by shape, not by a duty cycle.** §12.1a's distinction: *a cadence that runs
 * whether or not there is work is banned; a one-shot that exists only while there is work to do is
 * not.* There is no timer in this file. The stream is held while a predicate over `Match.state` is
 * true and dropped when it stops being true, which is the same binding §2.6 gives the wake lock.
 *
 * ── The `readyState` guard is the phone-call defence ────────────────────────
 *
 * §8.1: *"A `track.readyState` guard re-acquires the stream on `visibilitychange → visible` and
 * after an interruption, so a post-phone-call recording does not silently capture nothing."* That
 * failure — a recording that appears to work and contains nothing — is the one worth engineering
 * against, because the user finds out about it after the match with nothing to replay.
 *
 * ── AUD-4, and why permission is asked at Pre-Match ─────────────────────────
 *
 * §8.1: *"The permission prompt fires at Pre-Match, not at Kickoff … The just-in-time moment for a
 * match-long capability is match setup — never a modal appearing at Kickoff or on the first Flag
 * tap, which is the worst possible instant on the worst possible surface."*
 *
 * ── §0.3, and what a missing microphone actually looks like ─────────────────
 *
 * `getUserMedia` is **secure-context-only** and is absent by being `undefined` rather than by
 * throwing. Over plain `http://` — which is how a phone is most easily reached in development —
 * `hasGetUserMedia` is simply `false`, alongside id minting, the writer lock, the wake lock and the
 * service worker. That is a VER-3 item-0 problem with the *environment*, not a case to work around
 * here: this module reports `unavailable`, the Flag bar takes FLAG-4's silent path, and a browser
 * pass over HTTP is not evidence of anything.
 */

const MODULE = 'microphone';

/**
 * What the product knows about the microphone right now.
 *
 * Deliberately **not** the Permissions API's four values. iOS Safari does not answer
 * `permissions.query({name:'microphone'})` at all (it throws a `TypeError`, which the platform seam
 * maps to `unsupported`), so a state machine keyed on that would have one reachable value on the
 * target and four on the development machine. These five are what the *product* can distinguish by
 * having tried.
 */
export type MicState =
  /** Not asked yet, and the platform can do it. The Pre-Match card offers the prompt. */
  | 'idle'
  /** `getUserMedia` resolved at least once in this process. */
  | 'granted'
  /** `getUserMedia` rejected — a denial, or the tab was not the active one. FLAG-4 applies. */
  | 'denied'
  /** AUD-4's *"Not now"*. Non-destructive and re-offerable; FLAG-4 applies meanwhile. */
  | 'declined'
  /** No `getUserMedia` at all: an insecure context (§0.3), or a browser without it. FLAG-4 applies. */
  | 'unavailable';

/** Whether an audio affordance can actually record, or must take FLAG-4's silent path. */
export function canRecord(state: MicState): boolean {
  return state === 'granted';
}

export interface AcquiredStream {
  readonly stream: MediaStreamLike;
  /** `true` when a healthy held stream was re-used — FLAG-1's zero-cost path. */
  readonly reused: boolean;
}

/**
 * The held stream, its health, and the AUD-4 permission state.
 *
 * One instance per process (see `recorder.ts`'s singleton). It is a class rather than module-level
 * state so a test can drive two independent sessions, and so the platform seam is a constructor
 * argument rather than a global the tests have to patch.
 */
export class MicrophoneSession {
  private held: MediaStreamLike | null = null;
  private state: MicState;

  /**
   * AUD-4 — *"audio affordances show a re-enable hint"*, **once**.
   *
   * In memory, and that is a declared decision rather than an oversight. `Setting` has a closed key
   * space (§11.8) and `data/repos/profile.ts` deliberately exposes no typed setting writer, because
   * BAK-5's settings surface belongs to build unit U-22. A hint that re-appears once per app launch
   * is the honest version of "one-time" that this unit can build without becoming a second writer of
   * a table whose one interesting property depends on every write being deliberate.
   */
  private hintShown = false;

  constructor(private readonly platform: AudioPlatform = browserAudioPlatform()) {
    this.state = platform.hasGetUserMedia && platform.hasMediaRecorder ? 'idle' : 'unavailable';
  }

  micState(): MicState {
    return this.state;
  }

  /** True while a stream is held and every track is still `live` (§8.1's `readyState` guard). */
  isHealthy(): boolean {
    if (!this.held) return false;
    const tracks = this.held.getAudioTracks();
    return tracks.length > 0 && tracks.every((t) => t.readyState === 'live');
  }

  /** The held stream, or `null` when none is held or its tracks have ended. */
  current(): MediaStreamLike | null {
    return this.isHealthy() ? this.held : null;
  }

  /**
   * AUD-4 — the Pre-Match card's `Allow`.
   *
   * > *"`Allow` calls `getUserMedia` immediately, inside the tap's user activation."*
   *
   * Called synchronously from the tap handler, because iOS grants the prompt only inside a user
   * gesture. The stream it acquires is then **released**, since Pre-Match is not an in-match state
   * and NFR-5 forbids holding one there; what is kept is the *permission*, which is what makes
   * re-acquisition free at Kickoff.
   *
   * Never throws. A denial is an outcome the product is designed around (FLAG-4), not an error the
   * setup screen should surface as a failure.
   */
  async requestPermission(): Promise<MicState> {
    if (this.state === 'unavailable') {
      log.warn(MODULE, 'AUD-4: no getUserMedia in this context — see design §0.3', {
        hasGetUserMedia: this.platform.hasGetUserMedia,
        hasMediaRecorder: this.platform.hasMediaRecorder,
      });
      return this.state;
    }
    try {
      const stream = await this.platform.getUserMedia();
      this.state = 'granted';
      // Held only for the instant it took to prove the grant. §8.1: never outside in-match states.
      this.stopTracks(stream);
      log.info(MODULE, 'AUD-4: microphone permission granted at Pre-Match');
    } catch (cause) {
      this.state = 'denied';
      log.warn(MODULE, 'AUD-4: microphone permission was refused; FLAG-4’s silent path applies', {
        error: errorMessage(cause),
      });
    }
    return this.state;
  }

  /**
   * AUD-4 — the card's `Not now`.
   *
   * *"Non-destructive: flags still work as silent timestamped flags (FLAG-4)."* Distinct from
   * `denied` because it is the user's *deferral* rather than the platform's refusal, and the card
   * can offer the prompt again — a denial cannot be re-prompted from the page at all on iOS.
   */
  decline(): void {
    if (this.state === 'granted' || this.state === 'unavailable') return;
    this.state = 'declined';
    log.info(MODULE, 'AUD-4: microphone deferred — flags will be silent until it is enabled');
  }

  /**
   * AUD-4's re-enable hint, and whether this is the first time it has been due.
   *
   * Returns `true` **once** per process, the first time an audio affordance is used while the
   * microphone is not available. Callers render a hint on a `true` and nothing on a `false`, so
   * "one-time" is a property of this method rather than a rule every call site has to remember.
   */
  claimReEnableHint(): boolean {
    if (canRecord(this.state) || this.hintShown) return false;
    this.hintShown = true;
    return true;
  }

  /**
   * §8.1 — acquire on entry to an in-match state, or re-use the held stream.
   *
   * A stream whose tracks have gone `ended` — which is what a phone call does — is **dropped and
   * re-acquired** rather than re-used, because a `MediaRecorder` built on an ended track produces a
   * recording that runs, finalizes, and contains nothing.
   *
   * @throws {EnvironmentError} when acquisition fails. The caller decides what to do; FLAG-4's
   *   silent path is the Flag bar's answer, and it needs to know the difference between "no
   *   microphone" and "a microphone that produced nothing".
   */
  async acquire(): Promise<AcquiredStream> {
    if (this.state === 'unavailable') {
      throw new EnvironmentError(
        'This browser has no microphone access. On a development machine this is usually an insecure origin — getUserMedia is secure-context-only and is absent rather than failing (design §0.3).',
      );
    }

    const healthy = this.current();
    if (healthy) return { stream: healthy, reused: true };

    if (this.held) {
      log.warn(MODULE, 'the held stream was not live; re-acquiring (§8.1 readyState guard)', {
        states: this.held.getAudioTracks().map((t) => t.readyState),
      });
      this.stopTracks(this.held);
      this.held = null;
    }

    let stream: MediaStreamLike;
    try {
      stream = await this.platform.getUserMedia();
    } catch (cause) {
      this.state = 'denied';
      throw new EnvironmentError(
        `The microphone could not be opened: ${errorMessage(cause)}. On iOS this is usually a denied permission, or a Safari tab that is not the active one.`,
        { cause },
      );
    }

    this.held = stream;
    this.state = 'granted';
    log.info(MODULE, 'microphone acquired for the match', {
      label: stream.getAudioTracks()[0]?.label ?? null,
    });
    return { stream, reused: false };
  }

  /**
   * §8.1 / NFR-5 — release on exit from the in-match states, extinguishing the iOS indicator.
   *
   * Idempotent, because it is called from a state-change path that can fire more than once for one
   * transition (a reload after a Full Time, a `pagehide` on the way out).
   */
  release(): void {
    if (!this.held) return;
    this.stopTracks(this.held);
    this.held = null;
    log.info(MODULE, 'microphone released');
  }

  private stopTracks(stream: MediaStreamLike): void {
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch (cause) {
        // Reported, not swallowed. A track that will not stop leaves the iOS indicator lit, which is
        // worth knowing about at a venue even though nothing can be done about it there.
        log.warn(MODULE, 'track.stop() threw', { error: errorMessage(cause) });
      }
    }
  }
}
